import { usePairMarketScheduleStore } from '@ui/features/RWA/hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';
import { useSpreadPercentage } from '@ui/features/Spread/useSpreadPercentage';
import { useLeverage } from '@ui/features/Trade/store/useLeverage';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';

import {
  useMaxInputMaxLongOIUsd,
  useMaxInputMaxShortOIUsd,
} from '../components/TradeModule/hooks/useMaxInput';
import { useMinInputMinNotionalUsd } from '../components/TradeModule/hooks/useMinInput';
import { usePositionSize } from '../components/TradeModule/hooks/usePositionSize';
import { useFromInput } from './useFromInput';

export const useMarketStatus = () => {
  const leverage = useLeverage();
  const chainId = useDataChainId();
  const currentTradePairInfo = useTradingPair((v) => v.useTradingPair());
  const isMarketOpen = usePairMarketScheduleStore(
    (s) =>
      s.getMarketScheduleByPair(currentTradePairInfo.pairName, chainId)
        ?.isClientOpen,
  );

  const inputUsd = useFromInput((s) => s.input).value.usd; // fromUsd
  const positionSizeAfterFee = usePositionSize({
    inputUsd,
    // since this is open position, need to get the position size after open position's fees
    isBeforeFee: false,
  });

  const pairMarketPrice = usePriceLatest(currentTradePairInfo.baseSymbol);

  const spreadPercentageInfo = useSpreadPercentage({
    tradePair: currentTradePairInfo,
    positionSize: positionSizeAfterFee,
    pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
  });

  const maxInputMaxShortOIUsd = useMaxInputMaxShortOIUsd({
    pairInfos: currentTradePairInfo,
    leverage,
    spreadPercentage: spreadPercentageInfo.shortSpreadPercentage,
  });
  const maxInputMaxLongOIUsd = useMaxInputMaxLongOIUsd({
    pairInfos: currentTradePairInfo,
    leverage,
    spreadPercentage: spreadPercentageInfo.longSpreadPercentage,
  });

  const minInputMinNotionalUsdForShort = useMinInputMinNotionalUsd();
  const minInputMinNotionalUsdForLong = useMinInputMinNotionalUsd();

  return useMemo(() => {
    const longStatus = checkDisable({
      isLong: true,
      isMarketOpen: !!isMarketOpen,
      minInputMinNotionalUsd: minInputMinNotionalUsdForLong,
      maxInputMaxShortOIUsd,
      maxInputMaxLongOIUsd,
    });
    const shortStatus = checkDisable({
      isLong: false,
      isMarketOpen: !!isMarketOpen,
      minInputMinNotionalUsd: minInputMinNotionalUsdForShort,
      maxInputMaxShortOIUsd,
      maxInputMaxLongOIUsd,
    });

    return {
      pairBaseName: currentTradePairInfo.baseName,
      status:
        longStatus.reason == shortStatus.reason ? longStatus.reason : null,
      isDisabled: longStatus.isDisabled && shortStatus.isDisabled,
      long: longStatus,
      short: shortStatus,
    };
  }, [
    currentTradePairInfo.baseName,
    isMarketOpen,
    maxInputMaxLongOIUsd,
    maxInputMaxShortOIUsd,
    minInputMinNotionalUsdForLong,
    minInputMinNotionalUsdForShort,
  ]);
};

export enum MarketStatus {
  MAX_LONG_OI_REACH,
  MAX_SHORT_OI_REACH,
  MARKET_CLOSED,
  MIN_MAX_SIZE_CONFLICT,
}

function checkDisable({
  isLong,
  isMarketOpen,
  maxInputMaxLongOIUsd,
  maxInputMaxShortOIUsd,
  minInputMinNotionalUsd,
}: {
  isMarketOpen: boolean;
  isLong: boolean;
  minInputMinNotionalUsd: Nullable<BigNumber>;
  maxInputMaxShortOIUsd: Nullable<BigNumber>;
  maxInputMaxLongOIUsd: Nullable<BigNumber>;
}):
  | {
      reason: MarketStatus;
      isDisabled: true;
    }
  | {
      reason: null;
      isDisabled: false;
    } {
  if (!isMarketOpen) {
    return {
      reason: MarketStatus.MARKET_CLOSED,
      isDisabled: true,
    };
  }

  if (isLong) {
    /**
     *
     * for max limit check, especially oi limit, can be negative (target limit usd < existing oi * marketPrice), minus value can be negative,
     *
     * Given this case, should not open trade or add collateral
     *
     * and can happens both long and short
     *  */
    if (maxInputMaxLongOIUsd?.lte(0)) {
      return {
        reason: MarketStatus.MAX_LONG_OI_REACH,
        isDisabled: true,
      };
    }

    if (maxInputMaxLongOIUsd?.lte(minInputMinNotionalUsd ?? 0)) {
      return {
        reason: MarketStatus.MIN_MAX_SIZE_CONFLICT,
        isDisabled: true,
      };
    }
  } else {
    if (maxInputMaxShortOIUsd?.lte(0)) {
      return {
        reason: MarketStatus.MAX_SHORT_OI_REACH,
        isDisabled: true,
      };
    }

    if (maxInputMaxShortOIUsd?.lte(minInputMinNotionalUsd ?? 0)) {
      return {
        reason: MarketStatus.MIN_MAX_SIZE_CONFLICT,
        isDisabled: true,
      };
    }
  }

  return {
    reason: null,
    isDisabled: false,
  };
}

export type UseMarketStatusReturn = ReturnType<typeof useMarketStatus>;
